import { useState } from 'react'
import { FieldPath, FieldValues, useController } from 'react-hook-form'
import { FieldProps } from './Field'
import { snakeToTitle } from '../../utils'
import clsx from 'clsx'
import Button from '../Button'
import ImageGalleryModal, { ImageUpload } from '../../views/campaign/editor/ImageGalleryModal'
import { useTranslation } from 'react-i18next'
import './ImageField.css'

const isSvgUrl = (url: string) => {
    try {
        return new URL(url).pathname.endsWith('.svg')
    } catch {
        return url.endsWith('.svg')
    }
}

type ImageFieldProps<X extends FieldValues, P extends FieldPath<X>> = FieldProps<X, P>

export default function ImageField<X extends FieldValues, P extends FieldPath<X>>({
    form,
    name,
    label,
    required,
}: ImageFieldProps<X, P>) {
    const { t } = useTranslation()
    const [isGalleryOpen, setIsGalleryOpen] = useState(false)

    const { field } = useController({
        control: form?.control,
        name,
    })

    const handleInsert = (image: ImageUpload) => {
        field.onChange(image.url)
        setIsGalleryOpen(false)
    }

    return (
        <label className="ui-text-input">
            <span>
                {label ?? snakeToTitle(name)}
                {required && <span style={{ color: 'red' }}>&nbsp;*</span>}
            </span>
            <div className="ui-image-field-preview">
                {field.value
                    ? <>
                        <img
                            src={field.value}
                            alt={String(label ?? name)}
                            className={clsx(isSvgUrl(field.value) && 'has-transparency')}
                            onClick={() => setIsGalleryOpen(true)}
                        />
                        <Button variant="secondary" size="small" onClick={() => field.onChange('')}>{t('remove')}</Button>
                    </>
                    : <Button variant="secondary" size="small" onClick={() => setIsGalleryOpen(true)}>{t('choose_image')}</Button>
                }
            </div>
            <ImageGalleryModal
                open={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                onInsert={handleInsert}
            />
        </label>
    )
}
